<template>
  <div class="table-custom-reports tblcustomreps f-column mw-0 mh-0 w-100">
    <div class="d-flex w-100 no-scroll-track position-relative mw-0">
      <div
        class="d-flex no-shrink mb-auto ml-auto"
        style="height: var(--app-height-6)"
      >
        <slot name="title_after"></slot>
      </div>
    </div>

    <TableBody
      ref="tablebody"
      :error="tcrud_error"
      :isInfinite="true"
      :items="tcrud_items"
      :loading="tcrud_loading"
      :loadingBottom="tcrud_loading && !!tcrud_offset"
      class="mt-2"
      @update:scroll="handleScroll"
    >
      <template #no_data>
        <div
          class="gw_box flex-1 f-column f-center"
          style="--clr-box-back: var(--clr-black-1)"
        >
          <IconAndText
            class="my-auto"
            icon="mdi-folder"
            :title="$t('data_table.custom_reports.no_items')"
          />
        </div>
      </template>
      <template #head_before>
        <TableHead
          :sortConf="tbl_sortConf"
          :thead="thead"
          class="large-scroll-track"
          @click:th="tbl_handleSort"
        />
      </template>
      <template #default="{ item, index }">
        <CustomReportsTableRow
          :thead="thead"
          :item="item"
          :index="index"
          @patch:name="({ name }) => patchName({ item, name })"
          @apply:item="applyReport(item)"
        />
      </template>
    </TableBody>
  </div>
</template>

<script>
import IconAndText from '@/components/Poker/Overlays/IconAndText'
import { throttle } from 'lodash'
import TableBody from '@/components/DataTables/v2/TableBody'
import TableHead from '@/components/DataTables/v2/TableHead'
import table_mix from '@/components/DataTables/table_mix'
import tcrud_mix from '@/components/DataTables/tcrud_mix'
import CustomReportsTableRow from '@/components/DataTables/v2/tables/CustomReportsTable/CustomReportsTableRow.vue'

export default {
  name: 'CustomReportsTable',
  components: {
    TableBody,
    TableHead,
    CustomReportsTableRow,
    IconAndText,
  },
  mixins: [tcrud_mix, table_mix],
  emits: ['apply:report'],
  data() {
    this.tcrud_conf = {
      getEndpoint: item => `aggregated-report/custom-report/${item.id}/`,
      getChannelId: () => {
        let channelId = this.$store.getters.bc_channels_ids.find(id => id.includes('floating_gmf'))
        channelId =
          channelId ||
          (this.$route.query.channelId?.includes('floating_gmf')
            ? this.$route.query.channelId
            : null)
        return channelId
      },
      idKey: 'id',
      apiVersion: 'v4',
    }
    this.tbl_persistSortKey = 'customreports_sort'
    this.tcrud_endpoint = 'aggregated-report/custom/history/'
    this.tcrud_mutation = 'api_custom_aggregated_report_table'
    this.tcrud_apiVersion = this.tcrud_conf.apiVersion
    return {
      // last_used, name, ordering_position
      tbl_sortConf: {
        sortKey: 'created_at',
        sortOrder: 'desc',
      },
      selectedRows: [],
      lastTouchedIdx: -1,

      tcrud_limit: 100,
      tcrud_offset: 0,
    }
  },
  created() {
    this.tbl_setSortConf()
    this.tcrud_getDataCheck()
  },
  beforeUnmount() {
    this.$store.commit('api_reset', { key: this.tcrud_mutation })
    this.$store.dispatch('api_cancel_pending_requests', {
      callIds: [this.tcrud_endpoint],
    })
  },
  methods: {
    applyReport(item) {
      this.$emit('apply:report', item)
    },
    handleScroll: throttle(function (e, scroller) {
      if (this.tcrud_loading || !this.tcrud_hasNext || !scroller) return
      const isBottom = scroller.scrollTop + scroller.offsetHeight > scroller.scrollHeight - 10
      if (isBottom) {
        this.tcrud_offset += this.tcrud_limit
        this.tcrud_getDataCheck({ mergeData: true })
      }
    }, 20),
    patchName({ item, name }) {
      const expected_item = {
        ...item,
        name,
      }
      this.tcrud_patchItem(
        {
          item,
          patchData: { name },
          expected_item,
        },
        'PUT',
      )
    },
  },
  computed: {
    thead() {
      const cols = {
        name: {
          name: this.$t('table_head.name'),
          sortKey: 'name',
          width: 14,
        },
        created_at: {
          name: this.$t('table_head.created_at'),
          width: 12,
          sortKey: 'created_at',
        },
        positions: {
          name: this.$t('table_head.positions'),
          width: 8,
        },
        model: {
          name: this.$t('table_head.model'),
          width: 8,
        },
        actions: {
          name: this.$t('table_head.actions'),
          minWidth: 14,
        },
      }
      const res = {}
      Object.keys(cols).forEach(key => {
        const col = cols[key]
        const width = col.width || col.minWidth
        res[key] = {
          ...col,
          cssStyle: !width
            ? ''
            : `
            --min-width-th: ${width}rem;
            ${col.minWidth ? '' : `--max-width-th: ${width}rem`};
            `,
          tdStyle: !width
            ? ''
            : `
            --min-width-td: ${width}rem;
            ${col.minWidth ? '' : `--max-width-td: ${width}rem`};
            `,
        }
      })
      return res
    },
    tcrud_apiData() {
      return this.$store.getters.api_get_data(this.tcrud_mutation)
    },
    tcrud_items() {
      const res = this.tcrud_apiData?.items || []
      return res
    },
    tcrud_hasNext() {
      return !!this.tcrud_apiData?.next
    },
    tcrud_apiCall() {
      return this.$store.getters.api_get(this.tcrud_mutation)
    },
    tcrud_loading() {
      return this.tcrud_apiCall?.loading
    },
    tcrud_error() {
      return this.tcrud_apiCall?.error
    },
    tcrud_params() {
      return {
        limit: this.tcrud_limit,
        offset: this.tcrud_offset,
        ordering: `${this.tbl_sortConf.sortOrder === 'desc' ? '-' : ''}${
          this.tbl_sortConf.sortKey
        }`,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.tblcustomreps {
  display: flex;
  min-height: 0;
  flex-direction: column;
  flex: 1;
}
</style>
